import { MutationFunction } from '@apollo/client';
import { ErrorResponse } from '@apollo/client/link/error';
import { ChildProps, graphql, withApollo } from '@apollo/client/react/hoc';
import styled from '@emotion/styled/macro';
import compose from 'lodash.flowright';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import {
  CreateChannelMutationResult,
  CreateChannelMutationVariables,
  GetMessagesQuery,
  OpenChannelMutationResult,
  OpenChannelMutationVariables,
} from '../generated/hooks';
import { createChannelMutation, openChannelMutation } from '../graphql/mutations';
import { channelQuery, messagesQuery } from '../graphql/queries';
import auth from '../lib/auth';
import { parseUrlParams } from '../lib/common';
import { IChannel } from '../types';
import { PageTitleAndFavicon } from '../utils';
import Chat from './Chat';
import ErrorHandling from './ErrorHandling';
import { InputProvider } from './InputContext';

const PlaceHolder = styled.div``;

export enum Modals {
  CHANGE_LANGUAGE = 'CHANGE_LANGUAGE',
  REMOVE_RESPONDENT = 'REMOVE_RESPONDENT',
}

interface IDirectProps extends RouteComponentProps<any> {
  channelId?: string;
  accessKey?: string;
  setSomeReport: (someReport: boolean) => void;
}

interface IProps extends IDirectProps {
  surveyChat?: boolean;
  location: any;
  showModal: (
    modalName: Modals,
    show: boolean,
    redirectTo?: string,
  ) => (e: React.MouseEvent<HTMLElement> | null) => void;
  selectedLanguage: string;
  changeLanguage: (language: string) => void;
  toggleLanguageSelector: (hasAnswered: boolean) => void;
  setChannel: (channel: IChannel) => void;
}

interface IMutation {
  openChannel: MutationFunction<OpenChannelMutationResult, OpenChannelMutationVariables>;
  createChannel: MutationFunction<CreateChannelMutationResult, CreateChannelMutationVariables>;
  client: any;
}

interface IState {
  err: string | ErrorResponse | null;
  channel: IChannel | null;
  startingMessages: GetMessagesQuery['messages'] | null;
  isError: boolean | null;
  isOffline: boolean;
  displayLinkToOneplace: boolean;
  someUnfilledChannel: boolean;
  someReport: boolean;
}

class ChatWindow extends React.Component<ChildProps<IProps & IMutation>, IState> {
  private loadGeneration = 0;

  state: IState = {
    isError: null,
    err: null,
    channel: null,
    startingMessages: null,
    isOffline: false,
    displayLinkToOneplace: false,
    someUnfilledChannel: false,
    someReport: false,
  };

  componentDidMount() {
    this.openChannel();
  }

  componentDidUpdate(prevProps: ChildProps<IProps & IMutation>) {
    if (prevProps.channelId !== this.props.channelId) {
      this.setState({
        channel: null,
        startingMessages: null,
        isError: null,
        err: null,
      });
      this.openChannel();
    }
  }

  componentWillUnmount() {
    this.loadGeneration += 1;
  }

  private isStaleRequest = (requestGeneration: number) => requestGeneration !== this.loadGeneration;

  saveChannel = (
    channel: IChannel | null,
    startingMessages: GetMessagesQuery['messages'] | null,
    displayLinkToOneplace?: boolean | null,
    someUnfilledChannel?: boolean | null,
    someReport?: boolean | null,
    messages?: GetMessagesQuery['messages'] | null,
  ) => {
    if (channel) {
      // Populate channelQuery cache from the mutation result so Header/Messages skip the network call
      try {
        this.props.client.writeQuery({
          query: channelQuery,
          variables: { channelId: channel.id },
          data: { channel },
        });
      } catch (e) {
        console.warn('Failed to pre-populate channel cache', e);
      }
    }

    if (messages && channel) {
      // Returning user: populate messages cache from mutation result (skip getMessages network call)
      try {
        this.props.client.writeQuery({
          query: messagesQuery,
          data: { messages },
          variables: { channelId: channel.id },
        });
      } catch (e) {
        console.warn('Failed to pre-populate messages cache', e);
      }
    } else if (startingMessages && channel) {
      // First open: write null so typing animation isn't interrupted by a stale cache read
      try {
        this.props.client.writeQuery({
          query: messagesQuery,
          data: { messages: null },
          variables: { channelId: channel.id },
        });
      } catch (e) {
        console.warn('Failed to write null messages to cache', e);
      }
    }

    this.setState(() => ({
      isError: false,
      err: null,
      channel,
      startingMessages,
      displayLinkToOneplace: !!displayLinkToOneplace,
      someUnfilledChannel: !!someUnfilledChannel,
      someReport: !!someReport,
    }));
  };

  openChannel = async () => {
    const requestGeneration = ++this.loadGeneration;
    const { openChannel, channelId, accessKey, createChannel, location } = this.props;
    const params = parseUrlParams(location.search);
    const sourceId = params.getParam('sourceId');

    if (channelId || accessKey) {
      try {
        const localChannelId = localStorage.getItem(`channelForSurvey:${accessKey}${sourceId ? '/' + sourceId : ''}`);
        if ((accessKey && localChannelId == null) || localChannelId === '') {
          const queryResults: any = await createChannel({
            variables: { accessKey: accessKey ?? '', sourceId },
          });
          if (this.isStaleRequest(requestGeneration)) {
            return;
          }
          this.saveChannel(queryResults.data.createChannel.channel, queryResults.data.createChannel.startingMessages);
          this.props.setChannel(queryResults.data.createChannel.channel);
          this.props.setSomeReport(queryResults.data.createChannel.someReport);
          localStorage.setItem(
            `channelForSurvey:${accessKey}${sourceId ? '/' + sourceId : ''}`,
            queryResults.data.createChannel != null ? queryResults.data.createChannel.channel.id : '',
          );

          if (queryResults.data?.createChannel?.channel?.language) {
            this.props.changeLanguage(queryResults.data?.createChannel?.channel?.language.code);
          }
        } else {
          const queryResults: any = await openChannel({
            variables: {
              channelId: localChannelId != null && localChannelId !== '' ? localChannelId : channelId ?? '',
            },
          });
          if (this.isStaleRequest(requestGeneration)) {
            return;
          }

          if (queryResults.data.openChannel && queryResults.data.openChannel.channel.language) {
            this.props.changeLanguage(queryResults.data.openChannel.channel.language.code);
          }
          this.saveChannel(
            queryResults.data.openChannel.channel,
            queryResults.data.openChannel.startingMessages,
            queryResults.data.openChannel.displayLinkToOneplace,
            queryResults.data.openChannel.someUnfilledChannel,
            queryResults.data.openChannel.someReport,
            queryResults.data.openChannel.messages,
          );
          const respondent = queryResults.data.openChannel.channel?.respondent;
          if (respondent) {
            const email = respondent.user
              ? respondent.user.username
              : respondent.contacts
                  ?.filter((c: any) => c.type === 'EMAIL' && c.source === 'PRIMARY')
                  .map((c: any) => c.value)[0];
            if (email && (!auth.getUserEmail() || auth.getUserEmail() !== email)) {
              auth.setUserEmail(email);
            }
          }
          this.props.setSomeReport(queryResults.data.openChannel.someReport);
          this.props.setChannel(queryResults.data.openChannel.channel);
        }
      } catch (err) {
        if (this.isStaleRequest(requestGeneration)) {
          return;
        }
        this.setState(() => ({
          isError: true,
          err: ((err as any).graphQLErrors && (err as any).graphQLErrors[0]) || err,
        }));
      }
    } else {
      if (this.isStaleRequest(requestGeneration)) {
        return;
      }
      this.setState(() => ({
        isError: true,
        err: 'noURL',
      }));
    }
  };

  render() {
    const { isError, channel, startingMessages, err, displayLinkToOneplace, someUnfilledChannel, someReport } =
      this.state;
    const { client, toggleLanguageSelector, selectedLanguage } = this.props;

    return (
      <>
        <ErrorHandling err={err} refresh={this.openChannel} />
        <InputProvider channel={channel} startingMessages={startingMessages} client={client}>
          {!isError && channel ? (
            <Chat
              channel={channel}
              showModal={this.props.showModal}
              selectedLanguage={selectedLanguage}
              toggleLanguageSelector={toggleLanguageSelector}
              displayLinkToOneplace={displayLinkToOneplace}
              someUnfilledChannel={someUnfilledChannel}
              someReport={someReport}
              hasStartingMessages={(startingMessages || []).length > 0}
            />
          ) : (
            <PlaceHolder />
          )}
        </InputProvider>
        <PageTitleAndFavicon title={channel?.topicName} favicon="arnold" />
      </>
    );
  }
}

const WithChannelOperations = compose(
  graphql(openChannelMutation, {
    name: 'openChannel',
  }),
  graphql(createChannelMutation, {
    name: 'createChannel',
  }),
  withApollo,
);

export default withRouter<IDirectProps, any>(WithChannelOperations(ChatWindow));
